import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { deleteCoupon, getCoupons } from '../api/coupons';
import { Coupon, CouponSearchParams } from '../types/coupon';
import CouponSearchForm from '../components/coupon/CouponSearchForm';
import CouponTable from '../components/coupon/CouponTable';
import CouponRegistrationDialog from '../components/coupon/CouponRegistrationDialog';
import CouponEditDialog from '../components/coupon/CouponEditDialog';

const STORAGE_KEY = 'coupon_list_search';
const CouponList: React.FC = () => {
  // 検索条件を取得する関数
  const getSavedFilters = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('検索条件の復元に失敗:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };
  // 保存された状態または初期値で状態を初期化
  const savedFilters = getSavedFilters();
  const [ coupons, setCoupons ] = useState<Coupon[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // 検索フォームの展開状態（デフォルトは折りたたみ）
  const [ searchExpanded, setSearchExpanded ] = useState(savedFilters?.searchExpanded || false);
  // フォーム入力値（ユーザーの入力を保持）
  const [ searchTerm, setSearchTerm ] = useState(savedFilters?.searchTerm || '');
  const [ selectedStatus, setSelectedStatus ] = useState(savedFilters?.selectedStatus || '');
  const [ selectedDiscountType, setSelectedDiscountType ] = useState(savedFilters?.selectedDiscountType || '');
  // 実際の検索条件（検索ボタン押下時に更新）
  const [ searchParams, setSearchParams ] = useState({
    searchTerm: savedFilters?.searchTerm || '',
    selectedStatus: savedFilters?.selectedStatus || '',
    selectedDiscountType: savedFilters?.selectedDiscountType || '',
  });
  // ページネーション用の状態
  const [ page, setPage ] = useState(savedFilters?.page || 0);
  const [ rowsPerPage, setRowsPerPage ] = useState(savedFilters?.rowsPerPage || 20);
  const [ openRegistrationDialog, setOpenRegistrationDialog ] = useState(false);
  const [ openEditDialog, setOpenEditDialog ] = useState(false);
  const [ selectedCoupon, setSelectedCoupon ] = useState<Coupon | null>(null);
  const [ openDeleteDialog, setOpenDeleteDialog ] = useState(false);
  const [ couponToDelete, setCouponToDelete ] = useState<Coupon | null>(null);
  const [ deleteLoading, setDeleteLoading ] = useState(false);
  // クーポンデータを取得する関数
  const fetchCoupons = async(params: CouponSearchParams) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCoupons(params);
      setCoupons(data.results);
      setTotalCount(data.count);
    } catch (err) {
      console.error('クーポンデータの取得に失敗しました', err);
      setError('クーポンデータの取得に失敗しました。');
      setCoupons([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  // 検索実行関数
  const handleSearch = () => {
    setSearchParams({
      searchTerm,
      selectedStatus,
      selectedDiscountType,
    });
    setPage(0);
  };
  // 検索条件をクリア
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setSelectedDiscountType('');
    setSearchParams({
      searchTerm: '',
      selectedStatus: '',
      selectedDiscountType: '',
    });
    setPage(0);
    localStorage.removeItem(STORAGE_KEY);
  };
  // 検索フォームの展開/折りたたみを切り替える
  const handleToggleSearchExpand = () => {
    setSearchExpanded(!searchExpanded);
  };
  const handleOpenRegistrationDialog = () => {
    setOpenRegistrationDialog(true);
  };
  const handleCloseRegistrationDialog = () => {
    setOpenRegistrationDialog(false);
  };
  const handleRegistrationSuccess = () => {
    // 登録成功後、リストを更新
    setPage(0); // 最初のページに戻る
    const params: CouponSearchParams = {
      page: 1,
      page_size: rowsPerPage,
      ordering: '-created_at',
    };
    fetchCoupons(params);
  };
  const handleEditClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setOpenEditDialog(true);
  };
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedCoupon(null);
  };
  const handleEditSuccess = () => {
    // 編集成功後、現在のページを再読み込み
    const params: CouponSearchParams = {
      page: page + 1,
      page_size: rowsPerPage,
      ordering: '-created_at',
    };
    if (searchParams.searchTerm) {
      params.search = searchParams.searchTerm;
    }
    if (searchParams.selectedStatus) {
      params.status = searchParams.selectedStatus as CouponSearchParams['status'];
    }
    if (searchParams.selectedDiscountType) {
      params.discount_type = searchParams.selectedDiscountType as CouponSearchParams['discount_type'];
    }
    fetchCoupons(params);
  };
  const handleDeleteClick = (coupon: Coupon) => {
    setCouponToDelete(coupon);
    setOpenDeleteDialog(true);
  };
  const handleConfirmDelete = async() => {
    if (!couponToDelete) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await deleteCoupon(couponToDelete.id);
      setOpenDeleteDialog(false);
      setCouponToDelete(null);
      // 削除成功後、現在のページを再読み込み
      const params: CouponSearchParams = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: '-created_at',
      };
      if (searchParams.searchTerm) {
        params.search = searchParams.searchTerm;
      }
      if (searchParams.selectedStatus) {
        params.status = searchParams.selectedStatus as CouponSearchParams['status'];
      }
      if (searchParams.selectedDiscountType) {
        params.discount_type = searchParams.selectedDiscountType as CouponSearchParams['discount_type'];
      }
      fetchCoupons(params);
    } catch (err: any) {
      console.error('クーポン削除エラー:', err);
      const errorMessage = err.response?.data?.errors?.[0] || err.response?.data?.error || err.response?.data?.message || 'クーポンの削除に失敗しました。';
      setError(errorMessage);
      setOpenDeleteDialog(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setCouponToDelete(null);
  };
  // 検索条件が変更されたときにローカルストレージに保存し、データ取得を行う
  useEffect(() => {
    // ローカルストレージに保存
    const currentState = {
      searchTerm: searchParams.searchTerm,
      selectedStatus: searchParams.selectedStatus,
      selectedDiscountType: searchParams.selectedDiscountType,
      page,
      rowsPerPage,
      searchExpanded,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    // API検索パラメータの構築
    const params: CouponSearchParams = {
      page: page + 1, // APIは1-indexed
      page_size: rowsPerPage,
      ordering: '-created_at',
    };
    // 検索条件を適用
    if (searchParams.searchTerm) {
      params.search = searchParams.searchTerm;
    }
    if (searchParams.selectedStatus) {
      params.status = searchParams.selectedStatus as CouponSearchParams['status'];
    }
    if (searchParams.selectedDiscountType) {
      params.discount_type = searchParams.selectedDiscountType as CouponSearchParams['discount_type'];
    }
    fetchCoupons(params);
  }, [ page, rowsPerPage, searchParams, searchExpanded ]);
  // ページ変更ハンドラ
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // 1ページあたりの表示件数変更ハンドラ
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Box>
      <CouponSearchForm
        searchTerm={searchTerm}
        selectedStatus={selectedStatus}
        selectedDiscountType={selectedDiscountType}
        totalCount={totalCount}
        expanded={searchExpanded}
        onSearchChange={setSearchTerm}
        onStatusChange={setSelectedStatus}
        onDiscountTypeChange={setSelectedDiscountType}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        onClearSearch={() => setSearchTerm('')}
        onToggleExpand={handleToggleSearchExpand}
      />

      <Grid container spacing={3} sx={{ mb: 3, mt: 3 }}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon/>}
            onClick={handleOpenRegistrationDialog}
          >
            クーポン登録
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <CouponTable
        coupons={coupons}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      <CouponRegistrationDialog
        open={openRegistrationDialog}
        onClose={handleCloseRegistrationDialog}
        onSuccess={handleRegistrationSuccess}
      />

      {selectedCoupon && (
        <CouponEditDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          coupon={selectedCoupon}
          onSuccess={handleEditSuccess}
        />
      )}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">クーポンを削除しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {couponToDelete && (
              <>
                {couponToDelete.name ? (
                  <>「{couponToDelete.name}（{couponToDelete.code}）」</>
                ) : (
                  <>コード「{couponToDelete.code}」</>
                )}
                を削除します。
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleteLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            autoFocus
          >
            {deleteLoading ? '削除中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default CouponList;
